const SELECTOR = '[data-product-card-slider]';
const DRAG_THRESHOLD = 3;

let observer;

function hydrateImage(slide) {
  const image = slide?.querySelector('img[data-src]');
  if (!image) return;

  if (image.dataset.srcset) image.srcset = image.dataset.srcset;
  if (image.dataset.sizes) image.sizes = image.dataset.sizes;
  image.src = image.dataset.src;
  image.removeAttribute('data-src');
  image.removeAttribute('data-srcset');
  image.removeAttribute('data-sizes');
  image.classList.remove('is-deferred');
}

class ProductCardSlider {
  constructor(element) {
    this.element = element;
    this.track = element.querySelector('[data-product-card-track]');
    this.slides = Array.from(element.querySelectorAll('[data-product-card-slide]'));
    this.progress = element.querySelector('[data-product-card-progress]');
    this.previous = element.querySelector('[data-product-card-previous]');
    this.next = element.querySelector('[data-product-card-next]');
    this.status = element.querySelector('[data-product-card-status]');
    this.index = 0;
    this.scrollTimer = null;
    this.settleTimer = null;
    this.pointerStart = null;
    this.wasDragged = false;
    this.suppressLinkClickUntil = 0;
    this.mouseStart = null;
    this.touchStart = null;
    this.lastHandledGesture = 0;

    this.bind();
    this.update(0);
    observer?.observe(element);
  }

  bind() {
    this.previous.addEventListener('click', () => this.goTo(this.index - 1));
    this.next.addEventListener('click', () => this.goTo(this.index + 1));

    this.element.addEventListener('pointerenter', () => this.activate());
    this.element.addEventListener('focusin', () => this.activate());
    this.track.addEventListener('pointerdown', (event) => {
      this.activate();
      this.pointerStart = {
        id: event.pointerId,
        type: event.pointerType,
        x: event.clientX,
        y: event.clientY,
        scrollLeft: this.track.scrollLeft,
        index: this.index,
      };
      this.wasDragged = false;
      this.loadAround(this.index);

      if (event.pointerType === 'mouse' && event.button === 0) {
        this.track.setPointerCapture(event.pointerId);
        this.track.classList.add('is-dragging');
        document.body.classList.add('is-product-card-dragging');
      }
    });
    this.track.addEventListener('pointermove', (event) => {
      if (!this.pointerStart) return;
      const distanceX = event.clientX - this.pointerStart.x;
      if (Math.abs(distanceX) >= DRAG_THRESHOLD) this.wasDragged = true;

      if (this.pointerStart.type === 'mouse' && this.track.hasPointerCapture(this.pointerStart.id)) {
        event.preventDefault();
        this.track.scrollLeft = this.pointerStart.scrollLeft - distanceX;
      }
    });
    this.track.addEventListener('pointerup', (event) => this.finishDrag(event));
    this.track.addEventListener('pointercancel', (event) => this.finishDrag(event, true));
    this.track.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      this.touchStart = { x: touch.clientX, y: touch.clientY, index: this.index };
      this.activate();
    }, { passive: true });
    this.track.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch || !this.touchStart) return;

      const distanceX = touch.clientX - this.touchStart.x;
      const distanceY = touch.clientY - this.touchStart.y;
      const wasTouchDrag = Math.abs(distanceX) >= DRAG_THRESHOLD && Math.abs(distanceX) > Math.abs(distanceY);
      const startIndex = this.touchStart.index;
      this.touchStart = null;

      if (!wasTouchDrag) return;
      if (event.cancelable) event.preventDefault();
      this.suppressLinkClickUntil = performance.now() + 700;

      if (performance.now() - this.lastHandledGesture > 100) {
        this.lastHandledGesture = performance.now();
        this.goTo(startIndex + (distanceX < 0 ? 1 : -1), true);
      }
    }, { passive: false });
    this.track.addEventListener('touchcancel', () => {
      this.touchStart = null;
    }, { passive: true });
    this.track.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      this.mouseStart = { x: event.clientX, y: event.clientY, scrollLeft: this.track.scrollLeft, index: this.index };

      const moveMouseGesture = (mouseMoveEvent) => {
        if (!this.mouseStart) return;
        const distanceX = mouseMoveEvent.clientX - this.mouseStart.x;
        if (Math.abs(distanceX) < DRAG_THRESHOLD) return;

        mouseMoveEvent.preventDefault();
        this.wasDragged = true;
        this.track.scrollLeft = this.mouseStart.scrollLeft - distanceX;
      };

      const finishMouseGesture = (mouseUpEvent) => {
        const distanceX = this.mouseStart ? mouseUpEvent.clientX - this.mouseStart.x : 0;
        const distanceY = this.mouseStart ? mouseUpEvent.clientY - this.mouseStart.y : 0;
        const wasMouseDrag = Math.abs(distanceX) >= DRAG_THRESHOLD && Math.abs(distanceX) > Math.abs(distanceY);
        if (wasMouseDrag) {
          this.suppressLinkClickUntil = performance.now() + 700;
          if (performance.now() - this.lastHandledGesture > 50) {
            this.goTo(this.mouseStart.index + (distanceX < 0 ? 1 : -1), true);
          }
        }
        this.mouseStart = null;
        document.body.classList.remove('is-product-card-dragging');
        this.track.classList.remove('is-dragging');
        window.removeEventListener('mousemove', moveMouseGesture, true);
        window.removeEventListener('mouseup', finishMouseGesture, true);
      };

      window.addEventListener('mousemove', moveMouseGesture, true);
      window.addEventListener('mouseup', finishMouseGesture, true);
    });
    this.element.addEventListener('dragstart', (event) => event.preventDefault(), true);
    this.element.addEventListener('selectstart', (event) => {
      if (this.pointerStart?.type === 'mouse') event.preventDefault();
    }, true);
    this.element.addEventListener('click', (event) => {
      if (!event.target.closest('[data-product-card-track]')) return;

      if (performance.now() <= this.suppressLinkClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      if (this.element.dataset.productUrl) window.location.assign(this.element.dataset.productUrl);
    }, true);
    this.track.addEventListener('scroll', () => {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.syncToScroll(), 80);
      this.loadAround(this.getScrollIndex());
    }, { passive: true });
    this.element.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    });
  }

  finishDrag(event, cancelled = false) {
    if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
    const distanceX = event.clientX - this.pointerStart.x;
    const distanceY = event.clientY - this.pointerStart.y;
    const startIndex = this.pointerStart.index;
    const wasDragGesture = !cancelled && Math.abs(distanceX) >= DRAG_THRESHOLD && Math.abs(distanceX) > Math.abs(distanceY);

    if (wasDragGesture) {
      this.suppressLinkClickUntil = performance.now() + 700;
      this.lastHandledGesture = performance.now();
    }

    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    this.track.classList.remove('is-dragging');
    document.body.classList.remove('is-product-card-dragging');
    this.pointerStart = null;
    this.wasDragged = false;

    if (wasDragGesture) this.goTo(startIndex + (distanceX < 0 ? 1 : -1), true);
  }

  getScrollIndex() {
    if (!this.track.clientWidth) return this.index;
    return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
  }

  activate() {
    this.element.classList.add('is-slider-active');
    this.loadAround(this.index);
  }

  loadAround(index) {
    hydrateImage(this.slides[index]);
    hydrateImage(this.slides[index + 1]);
  }

  goTo(index, fromDrag = false) {
    const target = (index + this.slides.length) % this.slides.length;
    this.loadAround(target);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (fromDrag) {
      window.clearTimeout(this.settleTimer);
      this.track.classList.add('is-settling');
      this.settleTimer = window.setTimeout(() => {
        this.track.classList.remove('is-settling');
      }, reduceMotion ? 0 : 400);
    }

    this.track.scrollTo({ left: this.slides[target].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.update(target);
  }

  syncToScroll() {
    this.update(this.getScrollIndex());
  }

  update(index) {
    this.index = index;
    this.previous.disabled = false;
    this.next.disabled = false;
    this.progress?.style.setProperty('--slide-index', index);
    if (this.status) {
      this.status.textContent = this.element.dataset.statusTemplate
        .replace('%current%', index + 1)
        .replace('%total%', this.slides.length);
    }
  }
}

function getObserver() {
  if (observer || !('IntersectionObserver' in window)) return observer;
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.productCardSlider?.activate();
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '200px 0px' });
  return observer;
}

export default function initProductCardSliders(root = document) {
  getObserver();
  root.querySelectorAll(SELECTOR).forEach((element) => {
    if (element.productCardSlider) return;
    element.productCardSlider = new ProductCardSlider(element);
    if (!observer) element.productCardSlider.activate();
  });
}
